import { z } from 'zod';
/** Wire-level enum for the kinds of entity that can be the subject of a permission directive. */
export const AccessSourceType = z
    .enum(['firm', 'firm-membership', 'group', 'user', 'special', 'subscription', 'plan'])
    .meta({ id: 'AccessSourceType' });
// Special permission-subject literals. Plain string constants plus Zod literal schemas that
// participate in the `SpecialPermissionSubject` union below.
export const AuthenticatedUser = 'authenticated-users';
export const AnyUser = 'everyone';
export const PublicUser = 'anonymous-users';
export const SpecialPermissionSubject = z
    .union([z.literal(AuthenticatedUser), z.literal(AnyUser), z.literal(PublicUser)])
    .meta({ id: 'SpecialPermissionSubject' });
export const SpecialPermissionSubjects = [AuthenticatedUser, AnyUser, PublicUser];
const SpecialPermissionSubjectSet = new Set(SpecialPermissionSubjects);
export function isSpecialPermissionSubject(subject) {
    return SpecialPermissionSubjectSet.has(subject);
}
/** A UUID string or one of the three special-user literals. */
export const PermissionSubject = z.union([z.uuid(), SpecialPermissionSubject]).meta({ id: 'PermissionSubject' });
/** @deprecated Prefer {@link TypedPermissionDirective}. */
export const DenyPermission = z.object({ deny: PermissionSubject }).meta({ id: 'DenyPermission' });
/**
 * Wire-level counterpart of the backend-core `TypedPermissionDirective`. Carries the subject
 * type inline so downstream access expansion can skip type-resolution lookups.
 */
export const TypedPermissionDirective = z
    .object({
    mode: z.enum(['allow', 'deny']),
    type: AccessSourceType,
    id: PermissionSubject,
})
    .meta({ id: 'TypedPermissionDirective' });
/**
 * A single directive in a `Permissions` row. The typed variant is preferred; legacy (untyped)
 * variants still decode so documents written by older clients round-trip.
 */
export const PermissionDirective = z
    .union([TypedPermissionDirective, PermissionSubject, DenyPermission])
    .meta({ id: 'PermissionDirective' });
/** Type-guard: is this directive in the typed shape? */
export function isTypedPermissionDirective(directive) {
    return typeof directive === 'object' && directive !== null && 'mode' in directive;
}
/** The four-rung permission ladder, from least to most privileged. */
export const PermissionType = z.enum(['reader', 'writer', 'owner', 'admin']).meta({ id: 'PermissionType' });
/** Pluralised row-name form: `'readers' | 'writers' | 'owners' | 'admins'`. */
export const PermissionSetType = z.enum(['readers', 'writers', 'owners', 'admins']).meta({ id: 'PermissionSetType' });
export const Permissions = z
    .object({
    /** Ids of users/groups that can administer. */
    admins: z.array(PermissionDirective),
    /** Ids of users/groups that are owners. */
    owners: z.array(PermissionDirective),
    /** Ids of users/groups that can read. Include an `'everyone'` entry to grant public read. */
    readers: z.array(PermissionDirective),
    /** Ids of users/groups that can write. */
    writers: z.array(PermissionDirective),
})
    .meta({ id: 'Permissions' });
export const AppliedPermissionsFlags = z
    .object({
    /** @deprecated Use `effectiveAccess`. */
    canAdmin: z.boolean(),
    /** @deprecated Use `effectiveAccess`. */
    canRead: z.boolean(),
    /** @deprecated Use `effectiveAccess`. */
    canWrite: z.boolean(),
    /** @deprecated Use `effectiveAccess`. */
    doesOwn: z.boolean(),
    /** Can others besides the entity read? */
    isShared: z.boolean(),
    /**
     * Effective access for the security context, taking inheritance and firms into consideration.
     * This is non-nullable by design: callers of the mapping helpers must already have gated access
     * to the entity (e.g. via `throwIfPermissionDenied` or a filter-by-access query). If this field
     * would otherwise be `null`/`undefined`, the mapping layer throws so that ungated callers (which
     * would otherwise leak entity metadata to users without access) fail loud instead of silently
     * emitting `null`. See PLAT-11976 discussion.
     */
    effectiveAccess: PermissionType,
    /** Effective access considering only the context itself, not inherited grants. */
    effectiveDirectAccess: PermissionType.nullable(),
    /** Are the security context permissions more than a single allow entry? */
    isDirectAccessComplex: z.boolean(),
})
    .meta({ id: 'AppliedPermissionsFlags' });
export const AppliedPermissions = z
    .object({
    ...Permissions.shape,
    ...AppliedPermissionsFlags.shape,
})
    .meta({ id: 'AppliedPermissions' });
export const Permissioned = z
    .object({
    permissions: AppliedPermissions,
})
    .meta({ id: 'Permissioned' });
export const HasPermissionedFlags = z
    .object({
    permissions: AppliedPermissionsFlags,
})
    .meta({ id: 'HasPermissionedFlags' });
export const PermissionEntry = z
    .object({
    directive: PermissionDirective,
    access: PermissionType,
})
    .meta({ id: 'PermissionEntry' });
export const PermissionUpdateEntry = z
    .object({
    access: PermissionType,
    kind: z.enum(['allow', 'deny']),
})
    .meta({ id: 'PermissionUpdateEntry' });
//# sourceMappingURL=permissions.js.map